/* Confirmation-only hero geometry; never initializes registration or customer data. */
(() => {
  'use strict';
  const instances = new WeakMap();
  function init(hero) {
    if (instances.has(hero)) return;
    const fog = hero.querySelector('.confirmed-fog');
    const sharp = hero.querySelector('.confirmed-sharp');
    const frame = hero.querySelector('.confirmed-frame');
    const above = hero.querySelector('.confirmed-line-fog');
    const below = hero.querySelector('.confirmed-line-clear');
    const welcome = hero.querySelector('.confirmed-welcome');
    const heading = welcome && welcome.querySelector('h1');
    const top = hero.querySelector('.confirmed-ghost-top');
    const bottom = hero.querySelector('.confirmed-ghost-bottom');
    if (!fog || !sharp || !frame || !below || !welcome) return;
    let active = true;
    let fontsReady = !document.fonts || document.fonts.status === 'loaded';
    let pending = 0;
    const cleanups = [];
    function on(target, event, listener) {
      target.addEventListener(event, listener);
      cleanups.push(() => target.removeEventListener(event, listener));
    }
    function layout() {
      if (!active) return;
      const width = hero.clientWidth;
      const height = window.innerHeight;
      const desktop = width > 900;
      const scale0 = Math.max(width / 2400, height / 1792) * 1.03;
      let scale, x, y;
      if (desktop) {
        // Match the wide portrait framing in the supplied reference.
        scale = scale0 * 1.2;
        x = (width - 2400 * scale) / 2 - width * .02;
        y = (height - 1792 * scale0) * .22 + 560 * scale0 - 560 * scale;
      } else {
        const eyeY = Math.round(height * .36);
        scale = Math.max(width / 2400, .470 * (width / 390), eyeY / 560);
        x = width * .424 - 850 * scale;
        y = eyeY - 560 * scale;
      }
      x = Math.min(0, Math.max(width - 2400 * scale, x));
      y = Math.min(0, Math.max(height - 1792 * scale, y));
      [fog, sharp].forEach(image => {
        Object.assign(image.style, { width: `${2400 * scale}px`, height: `${1792 * scale}px`, left: `${x}px`, top: `${y}px` });
      });
      const windowWidth = desktop ? Math.round(width * .39) : Math.min(Math.min(572, Math.max(420, Math.round(width * .298))), Math.round(width * .86));
      const windowHeight = Math.round(windowWidth * .318);
      const pad = Math.round(Math.max(44, windowWidth * .115));
      const radius = Math.round(windowHeight * .12);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${windowWidth + pad * 2}" height="${windowHeight + pad * 2}"><defs><filter id="blur" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="13"/></filter></defs><rect x="${pad}" y="${pad}" width="${windowWidth}" height="${windowHeight}" rx="${radius}" fill="white" filter="url(#blur)"/></svg>`;
      const eyeX = width <= 680 ? width / 2 : x + (desktop ? 1570 : 850) * scale;
      const centerX = Math.min(width - windowWidth / 2 - 8, Math.max(windowWidth / 2 + 8, eyeX));
      const centerY = Math.max(windowHeight / 2 + 8, y + 560 * scale);
      sharp.style.setProperty('--mask-url', `url("data:image/svg+xml,${encodeURIComponent(svg)}")`);
      sharp.style.setProperty('--mw', `${windowWidth + pad * 2}px`);
      sharp.style.setProperty('--mh', `${windowHeight + pad * 2}px`);
      sharp.style.setProperty('--mx', `${centerX - x - windowWidth / 2 - pad}px`);
      sharp.style.setProperty('--my', `${centerY - y - windowHeight / 2 - pad}px`);
      Object.assign(frame.style, { left: `${centerX - windowWidth / 2}px`, top: `${centerY - windowHeight / 2}px`, width: `${windowWidth}px`, height: `${windowHeight}px` });
      if (above) Object.assign(above.style, { left: `${centerX - windowWidth / 2}px`, top: `${centerY - windowHeight / 2 - above.offsetHeight - 10}px` });
      Object.assign(below.style, { left: `${centerX + windowWidth / 2 - below.offsetWidth}px`, right: 'auto', top: `${centerY + windowHeight / 2 + 10}px` });
      welcome.style.removeProperty('width');
      if (heading) heading.style.removeProperty('font-size');
      if (desktop) {
        const availableWidth = Math.max(1, frame.offsetLeft - welcome.offsetLeft - 24);
        welcome.style.width = `${Math.min(welcome.offsetWidth, availableWidth)}px`;
        if (heading) {
          const textRange = document.createRange();
          textRange.selectNodeContents(heading);
          const textWidth = textRange.getBoundingClientRect().width;
          if (textWidth > availableWidth) {
            heading.style.fontSize = `${parseFloat(getComputedStyle(heading).fontSize) * availableWidth / textWidth}px`;
          }
        }
        welcome.style.top = `${centerY}px`;
        welcome.style.transform = 'translateY(-50%)';
      } else {
        welcome.style.transform = 'none';
        welcome.style.top = `${below.offsetTop + below.offsetHeight + Math.round(height * .035)}px`;
      }
      if (top && bottom) {
        const aboveTop = above ? above.offsetTop : centerY - windowHeight / 2 - 10;
        const gap = aboveTop - (top.offsetTop + top.offsetHeight);
        const ghostY = desktop ? below.offsetTop + below.offsetHeight + gap : welcome.offsetTop + welcome.offsetHeight + Math.round(height * .05);
        bottom.style.bottom = 'auto';
        bottom.style.top = `${ghostY}px`;
        hero.style.setProperty('--confirmed-height', `${Math.max(400, Math.round(ghostY + bottom.offsetHeight + 28), welcome.offsetTop + welcome.offsetHeight + 28)}px`);
      }
      if (fontsReady) {
        hero.classList.add('is-ready');
        const landing = hero.closest('.rdv7-landing');
        if (landing) landing.classList.remove('rdv7-intro-pending');
      }
    }
    function schedule() {
      if (pending) cancelAnimationFrame(pending);
      pending = requestAnimationFrame(() => { pending = 0; layout(); });
    }
    on(window, 'resize', schedule);
    if ('ResizeObserver' in window) {
      let observedWidth = hero.clientWidth;
      const observer = new ResizeObserver(() => {
        if (hero.clientWidth === observedWidth) return;
        observedWidth = hero.clientWidth;
        layout();
      });
      observer.observe(hero);
      cleanups.push(() => observer.disconnect());
    }
    on(fog, 'load', schedule);
    on(sharp, 'load', schedule);
    if (document.fonts) document.fonts.ready.then(() => { fontsReady = true; if (active) schedule(); });
    instances.set(hero, () => { active = false; cancelAnimationFrame(pending); cleanups.forEach(fn => fn()); instances.delete(hero); });
    schedule();
  }
  function initAll(scope) { scope.querySelectorAll('[data-rdv7-confirmed-hero]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-rdv7-confirmed-hero]').forEach(hero => { const cleanup = instances.get(hero); if (cleanup) cleanup(); }));
})();
